import { createAsyncThunk } from '@reduxjs/toolkit';

import { removeUndefined } from 'commonUtils/removeUndefined';

import { sendSentryClientErrorOnce } from 'shared/lib';
import type { TLoansCalculationData, TLoansContactData } from 'shared/types/TLoansService';

import { selectAllFormFields } from 'entities/creditForm';
import { getUserScoreSelector } from 'entities/scoring';
import { getStParentId, sendStLeadPostbackThunk } from 'entities/st-tracking';
import { collectWLOtherDataSelector } from 'entities/whiteLabels';

import { sendLoansCalculationDataApi } from '../model/sendLoansCalculationData.query';

import { formDataToLoansCalculationCustomerAdapter } from './formDataToLoansCalculationCustomerAdapter';

type TPayload = {
  sourceUrl: string;
  deviceType: TLoansContactData['client']['deviceType'];
  availableBanks: string[];
  calcId?: string;
  channel?: string;
  isBentoRankingModel?: boolean;
};

export const sendCalculationThunk = createAsyncThunk<
  void,
  TPayload,
  { state: Pick<Store, 'userScore' | 'creditForm' | 'whiteLabel' | 'user'> }
>('sendCalculation/sendCalculationThunk', async (payload, { dispatch, getState }) => {
  const { sourceUrl, deviceType, calcId, isBentoRankingModel, availableBanks, channel } = payload;
  const formFields = selectAllFormFields(getState());
  const otherWlData = collectWLOtherDataSelector(getState());
  const stParentId = getStParentId();
  const { score } = getUserScoreSelector(getState());

  const customer = formDataToLoansCalculationCustomerAdapter(formFields);

  const data: TLoansCalculationData = {
    customer: {
      ...customer,
      calcId: calcId || '',
    },
    client: {
      sourceUrl,
      deviceType,
      channel,
    },
    other: {
      availableBanks: availableBanks?.length,
      ...(isBentoRankingModel ? { isBentoRankingModel } : {}),
      ...(stParentId ? { stParentId } : {}),
      ...otherWlData,
    },
  };

  try {
    dispatch(
      sendStLeadPostbackThunk({
        transactionId: stParentId,
        fullName: customer.fullName || undefined,
        mobilePhone: customer.mobilePhone || undefined,
        scorePoint: String(score),
      }),
    );

    await dispatch(
      sendLoansCalculationDataApi.endpoints.sendLoansCalculationData.initiate(removeUndefined(data)),
    ).unwrap();
  } catch (error) {
    sendSentryClientErrorOnce('sendCalculationThunk', error);
  }
});
